import {t} from 'i18next';
import {
  AurDropdownButton,
  DropdownIconTypes,
} from '../../common/aur-button/AurDropdownButton';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import {Box, Typography} from '@mui/material';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {ReactElement, useEffect, useMemo, useState} from 'react';
import AurBreadcrumb from '../../common/aur-breadcrumb/AurBreadCrumb';
import {RootState} from '../../../redux/store';
import {ReactComponent as BackgroundBlur} from '../../../assests/svg/backgroundBlur.svg';
import SavedSearchPanel from '../../eca/SavedSearchPanel';
import {SavedSearchType} from '../../eca/ECASavedSearchSidePanelDrawer';
import {EciHomePageTabs, MIN_SEARCH_CHAR} from '../../../constants/constants';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import {getPermissionsbyClientId} from '../../../api/ClientManagementService';
import {
  PAGE_TYPE,
  useManageMenuByPermissions,
} from '../../../hooks/permissions/useManageMenuByPermissions';

const ManageSavedSearch: React.FC = () => {
  const navigate = useNavigate();
  const selectedIndex = 'Standard Index';
  const [selectedSearchDetailUI, setSelectedSearchDetailUI] =
    useState<ReactElement | null>(null);
  const [, setSelectedSavedSearch] = useState<SavedSearchType | null>(null);
  const [searchInput, setSearchInput] = useState<string>('');

  const {selectedProjectName: projectName} = useSelector(
    (state: RootState) => state.reports
  );

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const breadCrumbItems = useMemo(() => {
    const baseItems: {label: string; path?: string}[] = [
      {
        label: t('eca.ecaHomepage.coreEciHome'),
        path: `/eci-home-page?tab=${EciHomePageTabs.ECI_OVERVIEW}`,
      },
      {
        label: t('fieldManagement.manageMenu.manageSavedSearches'),
      },
    ];
    return baseItems;
  }, []);

  const {manageMenus} = useManageMenuByPermissions(PAGE_TYPE.Saved_Searches);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await getPermissionsbyClientId(clientId);
        if (response?.status !== 200) {
          navigate('/welcome-single-client');
        }
      } catch (error) {
        console.error(error);
        navigate('/welcome-single-client');
      }
    };
    fetchPermissions();
  }, [clientId, navigate]);

  const renderHeaderNButtons = useMemo(() => {
    return (
      <>
        <Typography
          tabIndex={0}
          className="typo-text display3_size overlay-title"
          data-testid="manage-overlay"
        >
          {t('eca.savedSearchManagement.labelSavedSearch')}
        </Typography>
        <Box className="button-wrapper">
          {manageMenus?.length > 0 && (
            <AurDropdownButton
              elementID={'manageButton'}
              buttonText={t('eca.ecaHomepage.manage')}
              type="continue"
              aria-live="polite"
              size="Medium"
              iconType="Arrow"
              iconSize="medium"
              iconClassName="client-profile-details-dropdown-icon"
              menuDirectionLeft={false}
              menuItemHeight="medium"
              menuItemWidth="mediumLarge"
              menuItemTextSize="small"
              dropDownIconType={DropdownIconTypes.Chev}
              menuItems={manageMenus}
            >
              <AurIcon
                icon={IconNames.ChevronDown}
                color={IconColors.Custom}
                customClassName="client-profile-details-dropdown-icon"
              />
            </AurDropdownButton>
          )}
        </Box>
      </>
    );
  }, [manageMenus]);

  return (
    <div className="saved-search-management-container">
      <BackgroundBlur aria-hidden="true" />
      <div className="overlay-layout">
        <Box
          className="breadcrumb-section overlay-breadcrumb"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <AurBreadcrumb items={breadCrumbItems} />
        </Box>

        <Typography
          tabIndex={0}
          data-testid="project-title"
          className="typo-text subhead3_size project-title"
        >
          {projectName}
        </Typography>
        <Box display={'flex'} justifyContent={'space-between'}>
          {renderHeaderNButtons}
        </Box>
      </div>
      <AurSearchBox
        label={t('fieldManagement.fieldTable.search')}
        parentClassName="search-field-input-div"
        classNameProps="text-field"
        minCharForSearch={MIN_SEARCH_CHAR}
        onSearch={setSearchInput}
        dataTestId="search-input"
      />
      <div className="saved-search-side-panel-drawer index-saved-search">
        <div className="content-container">
          <div className="panel-container">
            <div className="left-search-list-tree">
              <Box className="location-container">
                {' '}
                <Typography className="typo-text label2_size location-label" tabIndex={0}>
                  {t('ecaDocList.saveSearch.location')}
                </Typography>{' '}
              </Box>
              <SavedSearchPanel
                selectedIndex={selectedIndex}
                enableFlayout={false}
                renderSelectedSearchDetail={setSelectedSearchDetailUI}
                onPickSavedSearch={setSelectedSavedSearch}
                openForPickSavedSearch={false}
                enableSearch={false}
                searchInput={searchInput}
                folderDetailsView={true}
                showActions={true}
                manageSaved={true}
                manageSearchRename={true}
              />
            </div>
            <div className="right-search-detail">
              {selectedSearchDetailUI ?? null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageSavedSearch;
